import React, { useEffect, useState, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import userAPI from "../../api/UserApi";
import { showError } from "../../utils/toastUtils";

import Sidebar from "./Sidebar";
import Dashboard from "./Dashboard";
import Orders from "./Orders";
import Account from "./Account";
import Addresses from "./Addresses";
import ChangePassword from "./ChangePassword";

// Same fetch as sanwariyaLatest/src/pages/user/Account.js:
// userAPI.getDetails() -> res.code === 200 -> res.data
// Fetched once here (instead of per-tab) so the sidebar name and the
// settings form always agree, and every tab shares the same values.
const DashboardLayout = () => {
  const [activeTab, setActiveTab] = useState("overview");

  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const fetchMyDetails = useCallback(async () => {
    try {
      const res = await userAPI.getDetails();
      if (res.code === 200) {
        setUser(res.data);
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setLoadingUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMyDetails();
  }, [fetchMyDetails]);

  return (
    <section className="relative overflow-hidden bg-[var(--bg-secondary)] py-[80px]">
      <style>{`
        .acc-wrap { max-width: 1040px; margin: 0 auto; padding: 0 24px; }

        /* profile card */
        .acc-profile {
          background: #fff; border: 1px solid var(--card-border);
          border-radius: 6px; overflow: hidden; box-shadow: var(--card-shadow);
        }
        .acc-banner {
          height: 76px;
          background: linear-gradient(135deg, var(--color-primary-dark), var(--color-primary-light));
          position: relative;
        }
        .acc-avatar-wrap {
          width: 68px; height: 68px; border-radius: 50%;
          border: 3px solid #fff; overflow: hidden;
          position: absolute; bottom: -34px; left: 50%;
          transform: translateX(-50%);
          box-shadow: 0 4px 14px rgba(0,0,0,0.12);
        }

        /* nav btn */
        .acc-nav {
          width: 100%; display: flex; align-items: center; gap: 10px;
          padding: 10px 12px; border-radius: 3px; border: none;
          font-family: var(--font-subheading); font-size: 0.63rem;
          font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase;
          cursor: pointer; transition: all 0.22s ease;
          background: transparent; color: var(--color-text-secondary);
          text-align: left;
        }
        .acc-nav:hover  { background: var(--bg-secondary); color: var(--color-text-primary); }
        .acc-nav.active { background: var(--color-primary); color: #fff; box-shadow: 0 4px 14px rgba(166,60,6,0.26); }

        /* loyalty */
        .acc-bar-track { height: 4px; background: var(--bg-secondary); border-radius: 2px; overflow: hidden; margin-top: 5px; }
        .acc-bar-fill  { height: 100%; border-radius: 2px; background: linear-gradient(to right, var(--color-primary-light), var(--color-primary)); }

        /* generic card */
        .acc-card {
          background: #fff; border: 1px solid var(--card-border);
          border-radius: 6px; box-shadow: var(--card-shadow);
          transition: box-shadow 0.22s ease; overflow: hidden;
        }
        .acc-card:hover { box-shadow: var(--card-shadow-hover); }

        /* stat */
        .acc-stat { display: flex; flex-direction: column; align-items: center; padding: 14px 12px; flex: 1; text-align: center; border-right: 1px solid var(--card-border); }
        .acc-stat:last-child { border-right: none; }

        /* badge */
        .acc-badge {
          display: inline-flex; align-items: center; padding: 3px 10px;
          border-radius: 999px; font-family: var(--font-subheading);
          font-size: 0.52rem; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase;
        }

        /* order row */
        .acc-order {
          background: #fff; border: 1px solid var(--card-border); border-radius: 6px;
          padding: 16px 18px; display: flex; align-items: center; gap: 14px;
          flex-wrap: wrap; box-shadow: var(--card-shadow); transition: box-shadow 0.22s ease;
        }
        .acc-order:hover { box-shadow: var(--card-shadow-hover); }

        /* setting row */
        .acc-set-row {
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 14px; border-radius: 4px; cursor: pointer;
          transition: background 0.2s ease; gap: 12px;
        }
        .acc-set-row:hover { background: var(--bg-hover); }

        /* toggle */
        .acc-toggle {
          width: 42px; height: 22px; border-radius: 999px;
          position: relative; cursor: pointer; border: none; transition: background 0.28s ease; flex-shrink: 0;
        }
        .acc-thumb {
          width: 16px; height: 16px; border-radius: 50%; background: #fff;
          position: absolute; top: 3px; transition: left 0.28s ease;
          box-shadow: 0 1px 4px rgba(0,0,0,0.16);
        }

        /* buttons */
        .acc-btn-p {
          padding: 8px 16px; border-radius: 2px;
          background: var(--color-primary); color: #fff; border: 1px solid var(--color-primary);
          font-family: var(--font-subheading); font-size: 0.6rem; font-weight: 600;
          letter-spacing: 0.14em; text-transform: uppercase; cursor: pointer; transition: all 0.22s ease;
        }
        .acc-btn-p:hover { background: var(--color-primary-dark); border-color: var(--color-primary-dark); }
        .acc-btn-o {
          padding: 8px 16px; border-radius: 2px;
          background: transparent; color: var(--color-text-secondary); border: 1px solid var(--card-border);
          font-family: var(--font-subheading); font-size: 0.6rem; font-weight: 600;
          letter-spacing: 0.14em; text-transform: uppercase; cursor: pointer; transition: all 0.22s ease;
        }
        .acc-btn-o:hover { border-color: var(--color-primary); color: var(--color-primary); }

        /* eyebrow */
        .acc-eye {
          font-family: var(--font-subheading); font-size: 0.54rem; font-weight: 700;
          letter-spacing: 0.22em; text-transform: uppercase; color: var(--color-text-muted);
          display: block; padding-bottom: 8px; border-bottom: 1px solid var(--card-border); margin-bottom: 10px;
        }

        @media (max-width: 900px) {
          .acc-main-grid { grid-template-columns: 1fr !important; }
          .acc-two-col   { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* BG blobs */}
      <div className="absolute top-0 right-0 z-0 h-[380px] w-[380px] rounded-full bg-[rgba(245,195,165,0.14)] blur-[90px] pointer-events-none" />

      <div className="absolute bottom-0 left-0 z-0 h-[450px] w-[450px] rounded-full bg-[rgba(223,233,220,0.18)] blur-[80px] pointer-events-none" />

      <div className="acc-wrap relative z-10">

        <div className="acc-main-grid grid grid-cols-[1fr_2fr] gap-5 items-start">

          <Sidebar
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            user={user}
            loadingUser={loadingUser}
          />

          <div>
            <AnimatePresence mode="wait">
              {activeTab === "overview" && <Dashboard key="overview" user={user} />}
              {activeTab === "orders" && <Orders key="orders" />}
              {activeTab === "addresses" && <Addresses key="addresses" />}
              {activeTab === "settings" && (
                <Account
                  key="settings"
                  user={user}
                  loadingUser={loadingUser}
                  refreshUser={fetchMyDetails}
                />
              )}
              {activeTab === "changePassword" && <ChangePassword key="changePassword" />}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashboardLayout;